import { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import DateRangePicker from '@components/common/DateRangePicker';
import SearchInput from '@components/common/SearchInput';
import SelectInput from '@components/common/SelectInput';
import PageHeader from '@components/common/PageHeader';
import { api } from '@/api/corporation/api.ts';
import {
    CorporationListItem,
    CorporationListRes,
    CorporationListReq,
} from '@/api/corporation/type.ts';
import DataTable, {
    ColumnConfig,
    HeaderButton,
} from '@components/common/DataTable/DataTable.tsx';
import { PhoneUtils } from '@utils/PhoneUtils.ts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    objectToQueryString,
    queryStringToObject,
    typeConverters,
} from '@utils/Utils';

const CorporationList = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [filters, setFilters] = useState<CorporationListReq>(() => {
        const urlFilters = queryStringToObject<CorporationListReq>(
            searchParams,
            {
                joinType: typeConverters.number,
                userStatus: typeConverters.number,
                nowPage: typeConverters.number,
                pageSize: typeConverters.number,
            },
        );

        return {
            pageSize: 10,
            nowPage: 1,
            pageGroup: 10,
            keyword: '',
            joinType: -1,
            userStatus: -1,
            startDt: undefined,
            endDt: undefined,
            ...urlFilters, // URL 파라미터가 있으면 덮어쓰기
        };
    });

    const joinTypeOptions = [
        { value: '-1', label: '전체' },
        { value: '0', label: '네이버' },
        { value: '1', label: '카카오' },
        { value: '2', label: '구글' },
        { value: '9', label: 'ID' },
    ];

    const userStatusOptions = [
        { value: '-1', label: '전체' },
        { value: '0', label: '정상' },
        { value: '1', label: '정지' },
        { value: '2', label: '탈퇴' },
        { value: '3', label: '삭제' },
    ];

    const updateFilter = (key: keyof CorporationListReq, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            ...(key !== 'nowPage' && { nowPage: 1 }), // 페이지 외 필터 변경시 첫 페이지로
        }));
    };

    const handleDateChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;

        setFilters(prev => ({
            ...prev,
            startDt: start ? format(start, 'yyyy-MM-dd') : undefined,
            endDt: end ? format(end, 'yyyy-MM-dd') : undefined,
            nowPage: 1,
        }));
    };

    const handleSearch = (value: string) => {
        updateFilter('keyword', value);
    };

    const handlePageChange = (page: number) => {
        updateFilter('nowPage', page);
    };

    const handlePageSizeChange = (pageSize: number) => {
        updateFilter('pageSize', pageSize);
    };

    const handleJoinTypeChange = (joinType: string) => {
        updateFilter('joinType', parseInt(joinType));
    };

    const handleUserStateChange = (userStatus: string) => {
        updateFilter('userStatus', parseInt(userStatus));
    };

    const [corporationRes, setCorporationListRes] =
        useState<CorporationListRes>();

    const fetchCorporation = useCallback(async () => {
        const response = await api.corporationList(filters);
        setCorporationListRes(response);
    }, [filters]);

    const getCurrentFilterParams = (): string => {
        return objectToQueryString(filters, {
            defaultValues: {
                pageSize: 10,
                nowPage: 1,
                pageGroup: 10,
                keyword: '',
                joinType: -1,
                userStatus: -1,
            },
        });
    };

    const onClickRow = (row: CorporationListItem) => {
        console.log('클릭된 행:', row.userIdx);
        const currentParams = getCurrentFilterParams();
        navigate(
            `/corporation/${row.userIdx}?returnUrl=${encodeURIComponent(currentParams)}`,
        );
    };

    const JOIN_TYPE_MAP = {
        0: { text: '네이버', className: 'bd-green' },
        1: { text: '카카오', className: 'bg-secondary' },
        2: { text: '구글', className: 'bg-info' },
        9: { text: 'ID', className: 'bg-primary' },
    } as const;

    const USER_STATUS_MAP = {
        0: { text: '정상', className: 'bg-success' },
        1: { text: '정지', className: 'bg-danger' },
        2: { text: '탈퇴', className: 'bg-dark' },
        3: { text: '삭제', className: 'bg-info' },
    } as const;

    const columns: ColumnConfig<CorporationListItem>[] = [
        { key: 'userIdx', header: 'No' },
        { key: 'userName', header: '대행사명' },
        { key: 'userId', header: 'ID', clickable: true },
        {
            key: 'joinType',
            header: '가입유형',
            render: value => {
                const typeInfo = JOIN_TYPE_MAP[
                    value as keyof typeof JOIN_TYPE_MAP
                ] || { text: '알 수 없음', className: 'bg-light text-warning' };

                return (
                    <span className={`badge ${typeInfo.className}`}>
                        {typeInfo.text}
                    </span>
                );
            },
        },
        {
            key: 'userStatus',
            header: '회원상태',
            render: value => {
                const statusInfo = USER_STATUS_MAP[
                    value as keyof typeof USER_STATUS_MAP
                ] || { text: '알 수 없음', className: 'bg-light text-warning' };

                return (
                    <span className={`badge ${statusInfo.className}`}>
                        {statusInfo.text}
                    </span>
                );
            },
        },
        {
            key: 'phone',
            header: '전화번호',
            render: value => PhoneUtils.format(value), // PhoneUtils 사용
        },
        { key: 'email', header: '이메일' },
        { key: 'loginDt', header: '로그인일자', render: value => value || '-' },
        { key: 'regDt', header: '등록일자' },
    ];

    const headerButtons: HeaderButton[] = [
        {
            label: '등록',
            icon: 'ri-add-line',
            className: 'btn btn-sm btn-primary btn-wave',
            onClick: () => navigate('/corporation/add'),
        },
    ];

    // 초기 데이터 로드 및 의존성 변경시 재로드
    useEffect(() => {
        fetchCorporation();
    }, [fetchCorporation]);

    return (
        <>
            <PageHeader
                title="대행사관리"
                breadcrumbs={[
                    { label: '홈', href: '/' },
                    { label: '사용자관리' },
                    { label: '대행사관리', active: true },
                ]}
            />

            <div className="row mt-4">
                <div className="col-xl-12">
                    <div className="card custom-card">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-12 col-md-3">
                                    <DateRangePicker
                                        startDate={
                                            filters.startDt
                                                ? new Date(filters.startDt)
                                                : null
                                        }
                                        endDate={
                                            filters.endDt
                                                ? new Date(filters.endDt)
                                                : null
                                        }
                                        onChange={handleDateChange}
                                        placeholder="날짜 범위를 선택하세요"
                                    />
                                </div>
                                <div className="col-12 col-md-9">
                                    <SearchInput
                                        placeholder="대행사명, 담당자명, 연락처등을 입력해주세요."
                                        value={filters.keyword || ''}
                                        onSearch={handleSearch}
                                    />
                                </div>
                            </div>

                            <div className="row mt-2">
                                <div className="col-6">
                                    <SelectInput
                                        label="가입유형"
                                        options={joinTypeOptions}
                                        required={false}
                                        value={
                                            filters.joinType?.toString() || '-1'
                                        }
                                        onChange={handleJoinTypeChange}
                                    />
                                </div>
                                <div className="col-6">
                                    <SelectInput
                                        label="회원상태"
                                        options={userStatusOptions}
                                        required={false}
                                        value={
                                            filters.userStatus?.toString() ||
                                            '-1'
                                        }
                                        onChange={handleUserStateChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row ">
                <div className="col-xl-12">
                    {corporationRes && (
                        <DataTable<CorporationListItem>
                            data={corporationRes?.data}
                            columns={columns}
                            headerButtons={headerButtons}
                            paging={corporationRes?.paging}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            onClickRow={onClickRow}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default CorporationList;
